'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import ContactActions from '@/components/common/ContactActions';

interface ContactInfoProps {
  contact: {
    phone: string;
    email: string;
    website: string;
    address: string;
    city: string;
    state: string;
    zip: string;
  };
  hours: {
    day: string;
    open: string;
    close: string;
    isOpen: boolean;
  }[];
  mechanicName: string;
}

const ContactInfo = ({ contact, hours, mechanicName }: ContactInfoProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [showAllHours, setShowAllHours] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-xl shadow-elevation-md p-6 space-y-6">
        <div className="h-6 bg-muted rounded w-32" />
        <div className="space-y-3">
          <div className="h-4 bg-muted rounded w-full" />
          <div className="h-4 bg-muted rounded w-full" />
        </div>
      </div>
    );
  }

  const fullAddress = `${contact.address}, ${contact.city}, ${contact.state} ${contact.zip}`;
  const displayedHours = showAllHours ? hours : hours.slice(0, 3);

  return (
    <div className="bg-card rounded-xl shadow-elevation-md p-6 space-y-6">
      <h2 className="font-heading text-xl font-semibold text-text-primary">
        Contact Information
      </h2>

      <div className="space-y-4">
        <div className="flex items-start space-x-3">
          <Icon name="PhoneIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-caption text-xs text-text-secondary mb-1">Phone</p>
            <a
              href={`tel:${contact.phone}`}
              className="font-body text-sm text-text-primary hover:text-primary transition-smooth"
            >
              {contact.phone}
            </a>
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <Icon name="EnvelopeIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-caption text-xs text-text-secondary mb-1">Email</p>
            <a
              href={`mailto:${contact.email}`}
              className="font-body text-sm text-text-primary hover:text-primary transition-smooth break-all"
            >
              {contact.email}
            </a>
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <Icon name="GlobeAltIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-caption text-xs text-text-secondary mb-1">Website</p>
            <a
              href={contact.website}
              target="_blank"
              rel="noopener noreferrer"
              className="font-body text-sm text-text-primary hover:text-primary transition-smooth break-all"
            >
              {contact.website}
            </a>
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <Icon name="MapPinIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-caption text-xs text-text-secondary mb-1">Address</p>
            <p className="font-body text-sm text-text-primary">
              {fullAddress}
            </p>
          </div>
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-base font-semibold text-text-primary">
            Business Hours
          </h3>
          {hours.length > 3 && (
            <button
              onClick={() => setShowAllHours(!showAllHours)}
              className="font-caption text-xs text-primary hover:underline"
            >
              {showAllHours ? 'Show Less' : 'Show All'}
            </button>
          )}
        </div>
        <div className="space-y-2">
          {displayedHours.map((hour, index) => (
            <div key={index} className="flex items-center justify-between">
              <span className="font-body text-sm text-text-secondary">
                {hour.day}
              </span>
              <div className="flex items-center space-x-2">
                <span className="font-body text-sm text-text-primary">
                  {hour.open} - {hour.close}
                </span>
                {hour.isOpen && (
                  <span className="flex h-2 w-2 rounded-full bg-success" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <ContactActions
        variant="detail"
        phoneNumber={contact.phone}
        address={fullAddress}
        mechanicName={mechanicName}
        className="border-t border-border pt-6"
      />
    </div>
  );
};

export default ContactInfo;